"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { requestSignup } from "@/actions/auth";
import { useTranslations } from "next-intl";

// ─── Schema ──────────────────────────────────────────────────────────────────

const signupSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  orgName: z
    .string()
    .trim()
    .max(80, "Organisation name must be 80 characters or fewer")
    .optional()
    .or(z.literal("")),
});

type SignupFormData = z.infer<typeof signupSchema>;

// ─── Error mapping ───────────────────────────────────────────────────────────

const ERROR_KEYS: Record<string, string> = {
  INVALID_ORG_NAME: "invalidOrgName",
  SLUG_TAKEN: "slugTaken",
  RATE_LIMIT_EXCEEDED: "rateLimitExceeded",
};

// ─── Component ───────────────────────────────────────────────────────────────

export function SignupForm() {
  const t = useTranslations("auth.signupForm");
  const [confirmation, setConfirmation] = useState<{
    email: string;
    slug?: string;
  } | null>(null);

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: { email: "", orgName: "" },
  });

  const isPending = form.formState.isSubmitting;

  const orgNameValue = form.watch("orgName");
  const hasOrgName = !!orgNameValue?.trim();

  async function onSubmit(data: SignupFormData) {
    const trimmedOrg = data.orgName?.trim() || undefined;
    const result = await requestSignup(data.email, trimmedOrg);
    if (result.success) {
      setConfirmation({ email: data.email, slug: result.slug });
    } else if (!result.success) {
      const key = ERROR_KEYS[result.error];
      const message = key
        ? t(key)
        : process.env.NODE_ENV !== "production"
          ? result.error
          : t("genericError");
      form.setError("root", { message });
    }
  }

  // ── Confirmation panel ───────────────────────────────────────────────────
  if (confirmation) {
    return (
      <div
        data-testid="signup-confirmation"
        className="border-border bg-card rounded-xl border p-8 text-center"
      >
        <div className="bg-status-success mx-auto mb-4 flex size-12 items-center justify-center rounded-full">
          <CheckCircle2 className="text-status-success-fg size-6" />
        </div>
        <h2 className="text-base font-semibold">{t("confirmationTitle")}</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          {t("confirmationBody", { email: confirmation.email })}
        </p>
        {confirmation.slug ? (
          <p className="text-muted-foreground mt-3 text-sm">
            {t("slugPreview", { slug: confirmation.slug })}
          </p>
        ) : (
          <p className="text-muted-foreground mt-3 text-sm">
            {t("communityConfirmation")}
          </p>
        )}
        <Link
          href="/login"
          className="text-primary mt-4 inline-block text-sm font-medium hover:underline"
        >
          {t("backToSignIn")}
        </Link>
      </div>
    );
  }

  return (
    <div className="border-border bg-card rounded-xl border">
      <div className="p-6">
        <form
          data-testid="signup-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="signup-email">{t("emailLabel")}</Label>
            <Input
              id="signup-email"
              type="email"
              autoComplete="email"
              placeholder={t("emailPlaceholder")}
              aria-invalid={!!form.formState.errors.email}
              {...form.register("email")}
            />
            {form.formState.errors.email && (
              <p className="text-destructive text-xs">
                {form.formState.errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="signup-org-name">
              {t("orgNameLabelOptional")}
            </Label>
            <Input
              id="signup-org-name"
              type="text"
              placeholder={t("orgNamePlaceholder")}
              aria-invalid={!!form.formState.errors.orgName}
              {...form.register("orgName")}
            />
            <p className="text-muted-foreground text-xs">
              {t("orgNameHelper")}
            </p>
            {form.formState.errors.orgName && (
              <p className="text-destructive text-xs">
                {form.formState.errors.orgName.message}
              </p>
            )}
          </div>

          {form.formState.errors.root && (
            <p
              data-testid="signup-error"
              className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
            >
              {form.formState.errors.root.message}
            </p>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isPending}
            data-testid="signup-button"
          >
            {isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <>
                {hasOrgName ? t("submit") : t("submitStudent")}
                <ArrowRight className="size-4" />
              </>
            )}
          </Button>

          <p className="text-muted-foreground text-center text-sm">
            {t("hasAccount")}{" "}
            <Link
              href="/login"
              className="text-primary font-medium hover:underline"
            >
              {t("signInLink")}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
